import { SourcingEventStatus } from "../../../autogen/bff-api";

export type BucketKey =
  | "arbeid"
  | "kunngjort"
  | "evaluering"
  | "tildeling"
  | "fullfort";

// The sidebar count keys owned by the procurement buckets. NavBadgeKey (navConfig) is the
// superset that also covers the Needs (behov*) badges and is derived from this, so a bucket
// can only ever be assigned one of its own badge keys — never a stray Needs key.
export type BucketBadgeKey =
  | "anskArbeid"
  | "anskKunngjort"
  | "anskEvaluering"
  | "anskTildeling"
  | "anskFullfort";

// Single source of truth: every status maps to exactly one bucket. Because this is a
// Record keyed by SourcingEventStatus, a new enum value without a bucket fails to compile,
// so the sidebar counts and the list filters can never silently drop a status.
//
// Buckets are deliberately coarser than statuses to keep the sidebar short. Two folds:
//   - "Pending publication" + "Publication failed" -> "In progress": transient
//     pre-announcement states the buyer still owns.
//   - "Cancelled" -> "Completed": both are terminal, archive-like outcomes.
// Each folded status keeps its own colored status tag in the list (notably the red
// "Publication failed" and "Cancelled" tags), so the merge groups without hiding anything.
export const STATUS_BUCKET: Record<SourcingEventStatus, BucketKey> = {
  [SourcingEventStatus.Draft]: "arbeid",
  [SourcingEventStatus.PublicationPending]: "arbeid",
  [SourcingEventStatus.PublicationFailed]: "arbeid",
  [SourcingEventStatus.Published]: "kunngjort",
  [SourcingEventStatus.Evaluation]: "evaluering",
  [SourcingEventStatus.AwardDecision]: "tildeling",
  [SourcingEventStatus.Awarded]: "tildeling",
  [SourcingEventStatus.Closed]: "fullfort",
  [SourcingEventStatus.Cancelled]: "fullfort",
};

// Bucket -> presentation. badgeKey ties a bucket to its sidebar count; labelKey is the
// i18n key. Sidebar ORDER lives in navConfig.
export const BUCKET_META: Record<BucketKey, { labelKey: string; badgeKey: BucketBadgeKey }> = {
  arbeid: { labelKey: "In progress", badgeKey: "anskArbeid" },
  kunngjort: { labelKey: "Announced", badgeKey: "anskKunngjort" },
  evaluering: { labelKey: "Under evaluation", badgeKey: "anskEvaluering" },
  tildeling: { labelKey: "Award and standstill", badgeKey: "anskTildeling" },
  fullfort: { labelKey: "Completed", badgeKey: "anskFullfort" },
};

// Inverse of STATUS_BUCKET: bucket -> statuses. Drives the list filter tabs. Seeded with
// every bucket so the Record type is honest even for a (currently hypothetical) bucket that
// no status maps to — callers get [] rather than undefined.
export const STATUSES_BY_BUCKET: Record<BucketKey, SourcingEventStatus[]> = (
  Object.entries(STATUS_BUCKET) as [SourcingEventStatus, BucketKey][]
).reduce(
  (acc, [status, bucket]) => {
    acc[bucket].push(status);
    return acc;
  },
  Object.fromEntries(
    (Object.keys(BUCKET_META) as BucketKey[]).map((bucket) => [bucket, [] as SourcingEventStatus[]]),
  ) as Record<BucketKey, SourcingEventStatus[]>,
);

export const countBadgesByStatus = (
  events: { status: SourcingEventStatus }[],
): Partial<Record<BucketBadgeKey, number>> => {
  const badges: Partial<Record<BucketBadgeKey, number>> = {};
  for (const event of events) {
    // Skip rather than crash if the BFF returns a status this build's enum doesn't know
    // yet (deploy ahead of webapp codegen) — the status tags degrade the same way.
    const badgeKey = BUCKET_META[STATUS_BUCKET[event.status]]?.badgeKey;
    if (!badgeKey) continue;
    badges[badgeKey] = (badges[badgeKey] ?? 0) + 1;
  }
  return badges;
};

// The ?status= URL slug IS a BucketKey. Build and validate it through these helpers so the
// nav links and the list filter can't drift from the bucket set with a stray string.
export const bucketQuery = (bucket: BucketKey): string => `status=${bucket}`;

export const isBucketKey = (slug: string | null): slug is BucketKey =>
  slug != null && Object.prototype.hasOwnProperty.call(BUCKET_META, slug);
